import React, { useCallback, useMemo } from 'react'

import { Modal, T, VStack } from 'govwell-ui'
import { ScrapeTemplateFragment } from 'types/graphql'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { useStartScrapeNowMutation } from 'src/fetch/scrapes'
import {
  useCreateScrapeTemplateMutation,
  useUpdateScrapeTemplateMutation,
} from 'src/fetch/scrapeTemplates'
import { useAppForm } from 'src/hooks/use-app-form'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { RefetchFnType } from 'src/types'
import { useAnnounceFirstInvalidField } from 'src/utils/forms'

type FormValues = {
  startUrl: string
  excludeUrlGlobs: string[]
}

type Props = {
  onClose: () => void
  existingScrapeTemplate?: ScrapeTemplateFragment
  refetch: RefetchFnType
}

const ScrapeTemplateModal = ({ onClose, existingScrapeTemplate, refetch }: Props) => {
  const { mutateAsync: createScrapeTemplate } = useCreateScrapeTemplateMutation()
  const { mutateAsync: updateScrapeTemplate } = useUpdateScrapeTemplateMutation()
  const { mutateAsync: startScrapeNow } = useStartScrapeNowMutation()
  const { notification } = useNotification()

  const announceFirstInvalidField = useAnnounceFirstInvalidField()
  const organizationId = useMyOrgId()

  const isEditMode = !!existingScrapeTemplate

  const initialValues: FormValues = useMemo(() => {
    if (existingScrapeTemplate) {
      return {
        startUrl: existingScrapeTemplate.startUrl,
        excludeUrlGlobs: existingScrapeTemplate.excludeUrlGlobs || [],
      }
    }
    return {
      startUrl: '',
      excludeUrlGlobs: [],
    }
  }, [existingScrapeTemplate])

  const form = useAppForm({
    defaultValues: initialValues,
  })

  const handleSubmit = useCallback(async () => {
    await form.handleSubmit()
    if (!form.state.isValid) {
      announceFirstInvalidField()
      return Promise.reject()
    }

    const input = form.state.values

    if (existingScrapeTemplate) {
      await updateScrapeTemplate({ id: existingScrapeTemplate.id, input })
    } else {
      const createScrapeTemplateResult = await createScrapeTemplate({
        input: { ...input, organizationId },
      })
      // Attempt to start the initial scrape. If it doesn't work, show an error toast but allow the modal to close.
      try {
        await startScrapeNow({
          scrapeTemplateId: createScrapeTemplateResult.createScrapeTemplate.id,
        })
      } catch (error) {
        notification.error({
          message: 'Failed to start scrape',
          description: error instanceof Error ? error.message : 'Please contact GovWell support.',
        })
      }
    }

    await refetch()
    onClose()
  }, [
    form,
    existingScrapeTemplate,
    updateScrapeTemplate,
    createScrapeTemplate,
    refetch,
    onClose,
    announceFirstInvalidField,
    startScrapeNow,
    notification,
    organizationId,
  ])

  return (
    <Modal onClose={onClose}>
      <Modal.Header>{isEditMode ? 'Edit External Website' : 'Add External Website'}</Modal.Header>
      <Modal.Content>
        <VStack gap={4}>
          <T.Body>Enter the URL of the external website you want to scrape. </T.Body>
          <form.AppField
            name="startUrl"
            validators={{
              onBlur: ({ value }) => {
                if (!URL.canParse(value)) {
                  return 'Please enter a valid URL.'
                }
                if (value.endsWith('/')) {
                  return 'Please remove the trailing slash.'
                }
              },
            }}
          >
            {(field) => (
              <field.Input label="URL" placeholder="https://example.com" field={field} isRequired />
            )}
          </form.AppField>
          <>
            <T.BodySmall>
              The scraper will access the page at this URL and use it as the starting point of a web
              crawl.
            </T.BodySmall>
            <T.BodySmall>
              During the crawl, any links that match this URL (whether http or https, www or
              non-www) will be accessed, and those pages will be added to the crawl recursively.
            </T.BodySmall>
          </>
        </VStack>
      </Modal.Content>
      <Modal.Footer
        confirmText={isEditMode ? 'Save changes' : 'Add external website'}
        onConfirm={handleSubmit}
      />
    </Modal>
  )
}

export default React.memo(ScrapeTemplateModal)
